import React, { memo, useCallback, useContext, useMemo, useState } from 'react';
import { Tooltip, Button, message, Modal, Dropdown, Menu } from 'antd';
import { CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';
import { RolesTypes } from '@utils/rolesConstants';
import './styles.less';

const { confirm } = Modal;

export interface ReportItemsProps {
  path: string;
  type: string;
  method: 'post' | 'get';
  visible?: boolean;
  params?: { [key: string]: any };
  filters?: { [key: string]: any };
  role?: RolesTypes;
  errorMessage?: string;
  disabled?: boolean;
  action?: () => void;
}

interface IRequestReport {
  items: ReportItemsProps[];
}

const RequestReport = ({ items }: IRequestReport) => {
  const { t, i18n } = useTranslation('reports');
  const navigate = useNavigate();
  const { effectiveRoles } = useContext(SessionContext);

  const [isRequesting, setIsRequesting] = useState(false);

  const hasRole = useCallback(
    (role: RolesTypes) => effectiveRoles?.portal?.includes(role),
    [effectiveRoles],
  );

  const itemsFiltered = useMemo(
    () =>
      items?.filter(
        item => (item?.visible ?? true) && (!item.role || hasRole(item.role)),
      ),
    [hasRole, items],
  );

  const navigateToReports = useCallback(
    (type: string) => {
      message.destroy('report-requested');
      navigate(`/support/reports?type=${type}`);
    },
    [navigate],
  );

  const handleRequest = useCallback(
    ({ type, path, filters, params, method = 'get' }: ReportItemsProps) => {
      setIsRequesting(true);
      api({
        method,
        url: `${path}?`,
        params: { ...params, locale: i18n.language },
        data: filters,
      })
        .then(() => {
          message.success({
            content: (
              <span>
                {t('request.report-success')}
                <Button
                  onClick={() => navigateToReports(type)}
                  type="link"
                  style={{ padding: 3 }}
                >
                  {t('request.go-to-reports')}
                </Button>
                {t('request.check-report-progress-and-download')}
              </span>
            ),
            key: 'report-requested',
            duration: 5,
          });
        })
        .catch(() => message.error(t('request.report-error')))
        .finally(() => setIsRequesting(false));
    },
    [i18n.language, navigateToReports, t],
  );

  const showExportConfirm = useCallback(
    (item: ReportItemsProps) => {
      confirm({
        title: <h3>{t('request.report')}</h3>,
        icon: <QuestionCircleOutlined />,
        content: t('request.description'),
        okText: t('request.buttons.confirm'),
        cancelText: t('request.buttons.cancel'),
        okButtonProps: { type: 'primary' },
        onOk: () => handleRequest(item),
        afterClose() {
          document.body.style.removeProperty('overflow');
        },
      });
    },
    [handleRequest, t],
  );

  const menu = useMemo(
    () => (
      <Menu>
        <Menu.ItemGroup title={t('title')}>
          {itemsFiltered?.map(item => (
            <Menu.Item
              key={item.type}
              onClick={() => {
                item?.action ? item?.action() : showExportConfirm(item);
              }}
            >
              {t(`types.${item.type.toUpperCase()}`)}
            </Menu.Item>
          ))}
        </Menu.ItemGroup>
      </Menu>
    ),
    [itemsFiltered, showExportConfirm, t],
  );

  const button = useMemo(
    () => (
      <Button
        icon={<CopyOutlined />}
        disabled={isRequesting || itemsFiltered?.[0]?.disabled}
        loading={isRequesting}
        onClick={e => {
          e.stopPropagation();
          if (itemsFiltered?.length === 1) {
            if (itemsFiltered?.[0]?.action) {
              itemsFiltered?.[0]?.action();
              return;
            }
            showExportConfirm(itemsFiltered?.[0]);
          }
        }}
        className="report-request-button"
      />
    ),
    [isRequesting, showExportConfirm, itemsFiltered],
  );

  if (itemsFiltered?.length === 0) {
    return <></>;
  }

  return (
    <Tooltip
      title={
        itemsFiltered?.[0]?.disabled
          ? itemsFiltered?.[0]?.errorMessage
          : t('request.report')
      }
    >
      {itemsFiltered?.length > 1 ? (
        <Dropdown overlay={menu} arrow overlayClassName="overlay-menu">
          {button}
        </Dropdown>
      ) : (
        button
      )}
    </Tooltip>
  );
};

export default memo(RequestReport);
